const isPlainObject = (value: unknown): value is Record<string, unknown> =>
    Object.prototype.toString.call(value) === '[object Object]';

function mergeValue(targetValue: unknown, sourceValue: unknown): unknown {
    if (isPlainObject(sourceValue)) {
        // Recurse (into the target object, or a fresh one if the slot is empty) so nested
        // plain objects are deep-copied rather than shared by reference — matching lodash `merge`.
        return deepMerge(isPlainObject(targetValue) ? targetValue : {}, sourceValue);
    }
    if (Array.isArray(sourceValue)) {
        // lodash `merge` merges arrays by index, preserving any trailing target elements and
        // deep-copying source elements (so no source subtree is shared by reference).
        const merged = Array.isArray(targetValue) ? [...targetValue] : [];
        sourceValue.forEach((value, index) => {
            merged[index] = value === undefined ? merged[index] : mergeValue(merged[index], value);
        });
        return merged;
    }
    return sourceValue;
}

/**
 * Native replacement for lodash/fp `merge`. Recursively merges own enumerable
 * properties of `source` into a shallow-then-deep copy of `target`, without
 * mutating either input (matching lodash/fp's immutable `merge`). `undefined`
 * source values are skipped; plain objects and arrays are merged recursively.
 */
export default function deepMerge<Target extends object, Source extends object>(
    target: Target,
    source: Source,
): Target & Source {
    const output: Record<string, unknown> = { ...(target as Record<string, unknown>) };

    Object.keys(source ?? {}).forEach(key => {
        const sourceValue = (source as Record<string, unknown>)[key];
        if (sourceValue === undefined) return;
        output[key] = mergeValue(output[key], sourceValue);
    });

    return output as Target & Source;
}
